import { Component, Input, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { Offer } from 'app/services/offers/offer.model';
import { SaveDealStatus, UnacceptBorrowerChosenOffer } from 'app/store/deal/deal/deal.actions';
import {
  ClearOfferError,
  CreateOffer,
  PublishOffer,
  RemoveFromPortal,
  ToggleOffer,
  UpdateOffer,
  DeleteOffer,
  RecommendOffer,
} from 'app/store/offers/offer.actions';
import { OfferState } from 'app/store/offers/offer.state';
import { OfferOption } from 'ng2-app/models/dto/offer-option/offer-option.model';
import { OfferOptionsState } from 'app/store/offers/offer-options.state';
import { Observable, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OfferSnackBarDataComponent } from '../offer-snack-bar-data/offer-snack-bar-data.component';
import { OfferSnackBarType } from 'ng2-app/offer/offer-snack-bar-data/offer-snack-bar-data.interface';

@Component({
  selector: 'app-offer-smart',
  templateUrl: './offer-smart.component.html',
  styleUrls: ['./offer-smart.component.scss'],
  standalone: false
})
export class OfferSmartComponent implements OnInit {

  @Select(OfferState.loading) loadingOffers$: Observable<boolean>;
  @Select(OfferState.error) updateOfferError$: Observable<string>;
  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;
  @Select(OfferOptionsState.offerOptions) offerOptions$: Observable<OfferOption[] | null>;

  @Input() dealId: number;
  @Input() dealStage: string;
  @Input() dealStatus: string;
  @Input() loanProductType: string;
  @Input() readOnly: boolean = false;
  @Input() hasContractRequest: boolean = false;
  @Input() advancedDeal: boolean = false;

  user: LenderUser;
  dealOffers$: Observable<Offer[]>;
  fundedOffer$: Observable<Offer[]>;
  acceptedOfferId$: Observable<number | null>;
  dealOptions$: Observable<OfferOption[]>;

  constructor(private store: Store, private snackBar: MatSnackBar) { }


  async ngOnInit() {
    this.lenderUser$.pipe(take(1)).subscribe(user =>{
      this.user = user;
    });
    this.dealOffers$ = this.store.select(OfferState.dealOffers).pipe(map(filterFn => filterFn(this.dealId, this.dealStage)));
    this.fundedOffer$ = this.store.select(OfferState.fundedOffer).pipe(map(filterFn => filterFn(this.dealId)));
    this.acceptedOfferId$ = this.store.select(OfferState.acceptedOfferId).pipe(map(filterFn => filterFn(this.dealId)));
    this.dealOptions$ = this.store.select(OfferOptionsState.dealOptions).pipe(map(filterFn => filterFn(this.dealId)));
  }


  publishOffer(offer: Offer) {
    this.store.dispatch(new PublishOffer(offer.id, this.user.id, new Date().getTime())).pipe(
      take(1)
    ).subscribe(() => {
      this.store.dispatch(new SaveDealStatus(this.dealId, 'underwriting', 'offerToBorrower', '', [], []))
        .subscribe(() => this.openOfferSnackBar(offer, true, OfferSnackBarType.PUBLISH));
    });
  }

  removeFromPortal(offer: Offer) {
    this.store.dispatch(new RemoveFromPortal(offer.id)).pipe(
      take(1)
    ).subscribe(() => {
      if (this.dealStage !== 'underwriting' || this.dealStatus !== 'offerToBorrower') {
        this.openOfferSnackBar(offer, true, OfferSnackBarType.REMOVE_FROM_PORTAL);
        return;
      }

      combineLatest([
        this.dealOffers$,
        this.dealOptions$,
      ]).pipe(take(1)).subscribe(([offers, options]) => {
        const hasPublishedOffers = (offers || []).some((tempOffer) => Boolean(tempOffer.publishedToBorrower));
        const hasPublishedOptions = (options || []).some((option) => (
          Boolean(option.publishedToBorrower) && !['draft', 'declined'].includes(option.status)
        ));

        if (hasPublishedOffers || hasPublishedOptions) {
          this.openOfferSnackBar(offer, true, OfferSnackBarType.REMOVE_FROM_PORTAL);
          return;
        }

        this.store.dispatch(new SaveDealStatus(this.dealId, 'underwriting', 'offerReceived', '', [], []))
          .subscribe(() => this.openOfferSnackBar(offer, true, OfferSnackBarType.REMOVE_FROM_PORTAL));
      });
    });
  }

  canBypassOfferValidation() {
    return this.user?.permissions?.includes('bypassOfferValidation');
  }

  canEdit() {
    return (
      !this.readOnly
      && this.user
      && this.user.permissions.includes('editOffers')
    );
  }

  canEditInactiveOffers() {
    return (this.user && this.user.permissions.includes('editInactiveOffers'));
  }

  canDelete() {
    return this.user && this.user.permissions.includes('manageDeals');
  }

  canCreate() {
    return !this.readOnly && this.user && !this.user.roles?.includes('Account Executive');
  }

  acceptedToggle(offer: { id: number, dealId: number, checked: boolean, chosenByBorrower: boolean }) {
    if (!offer.checked && offer.chosenByBorrower) {
      this.store.dispatch(new UnacceptBorrowerChosenOffer(offer.dealId));
    } else {
      this.store.dispatch(new UpdateOffer(offer.id, { accepted: offer.checked ? new Date().getTime() : null }));
    }
  }

  expandToggle(offerId: number) {
    this.store.dispatch(new ToggleOffer(offerId));
  }

  createOffer(offer: Offer) {
    offer.dealId = this.dealId;
    this.store.dispatch(new CreateOffer(offer));
  }

  editOffer({ offer }) {
    this.store.dispatch(new UpdateOffer(offer.id, offer));
  }

  dismissAlert() {
    this.store.dispatch(new ClearOfferError());
  }

  openOfferSnackBar(offer: Offer, isSuccess: boolean, type: OfferSnackBarType) {
    this.snackBar.openFromComponent(OfferSnackBarDataComponent, {
      data: {
        offer,
        isSuccess: isSuccess,
        type,
      },
      duration: 3000,
    });
  }

  deleteOffer(id: number) {
    this.store.dispatch(new DeleteOffer(id));
  }

  recommendOffer(offer: { id: number, checked: boolean }) {
    this.store.dispatch(new RecommendOffer(offer.id, offer.checked));
  }

}
